import { useState, useMemo, useEffect, useRef, useCallback } from 'react'
import { Form, notification } from 'antd'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import type { RobotFormValues } from '@/components/form/RobotForm'
import {
  getRobotById,
  updateConfigRobot,
  updateRobotWorkingDays,
} from '@/api/robots.api'
import type { GeneralSettings } from '../../robot-position-setup/types'
import { DEFAULT_GENERAL_SCHEDULE } from '../../robot-position-setup/constants'
import {
  buildGeneralSchedulesFromRobot,
  buildRobotPayload,
  pickVoiceId,
} from '../../robot-position-setup/helpers'

type WorkingScheduleFormRow = NonNullable<
  RobotFormValues['workingSchedules']
>[number]

export function useRobotConfigForm(robotId: string) {
  const queryClient = useQueryClient()
  const [robotForm] = Form.useForm<RobotFormValues>()
  const deletedWorkingDayIdsRef = useRef<Set<string>>(new Set())
  const [liveValues, setLiveValues] = useState<Partial<RobotFormValues>>({})

  const [general, setGeneral] = useState<GeneralSettings>({
    schedules: [DEFAULT_GENERAL_SCHEDULE],
    minEnergy: '',
    energyUnit: '%',
  })

  const { data: robot, refetch: refetchDetail } = useQuery({
    queryKey: ['robot-config-detail', robotId],
    queryFn: ({ signal }) =>
      robotId ? getRobotById(robotId, signal) : Promise.resolve(null),
    enabled: !!robotId,
    refetchOnMount: 'always',
    staleTime: 0,
    gcTime: 0,
  })

  const localBlockers = useMemo<string[]>(() => {
    const b: string[] = []
    const systemPrompt = liveValues.systemPrompt ?? robot?.systemPrompt
    const guardrails = liveValues.guardrails ?? robot?.guardrails
    const voice = liveValues.voice ?? pickVoiceId(robot?.voice)
    const startupMode = liveValues.startupMode ?? robot?.startupMode

    if (!systemPrompt || !String(systemPrompt).trim()) b.push('Nhiệm vụ')
    if (!guardrails || !String(guardrails).trim()) b.push('Tuân thủ')
    if (!voice) b.push('Giọng robot')
    if (!startupMode) b.push('Hình thức khởi động')
    return b
  }, [robot, liveValues])

  const backendFlag = robot as
    | (typeof robot & { canActivate?: boolean; activationBlockers?: string[] })
    | null
  const blockers =
    backendFlag?.activationBlockers && backendFlag.activationBlockers.length > 0
      ? backendFlag.activationBlockers
      : localBlockers
  const hasEnoughInfo =
    typeof backendFlag?.canActivate === 'boolean'
      ? backendFlag.canActivate
      : blockers.length === 0

  const isActivated = robot?.activate === 1
  const showActivateButton = !!robot && !isActivated
  const activateTooltip = hasEnoughInfo
    ? undefined
    : `Chưa đủ thông tin để kích hoạt robot${blockers.length > 0 ? ` (thiếu: ${blockers.join(', ')})` : ''}`

  const robotInitialValues = useMemo<
    Partial<RobotFormValues> | undefined
  >(() => {
    if (!robot) return undefined
    const kbArray = Array.isArray((robot as any).knowledgeBases)
      ? (robot as any).knowledgeBases.map((kb: any) => ({
          value: kb.id ?? kb,
          label: kb.name,
        }))
      : []
    return {
      code: robot.code,
      name: robot.name,
      agentName: robot.agentName,
      knowledgeBases: kbArray,
      systemPrompt: robot.systemPrompt,
      guardrails: robot.guardrails,
      greeting: robot.voicePrompt?.greeting ?? robot.greeting,
      prompt60s: robot.voicePrompt?.idlePrompt,
      prompt120s: robot.voicePrompt?.idleGoodbye,
      preCallPrompt: robot.voicePrompt?.preCallPrompt,
      endPrompt: robot.voicePrompt?.postCallPrompt,
      timeout: robot.timeout,
      voice:
        typeof robot.voice === 'object'
          ? robot.voice.collectionId || robot.voice.id
          : robot.voice,
      startupMode: robot.startupMode,
      workTime: typeof robot.workTime === 'object' ? robot.workTime : undefined,
      robotWorkingDays: robot.robotWorkingDays,
      lowBatteryThreshold: robot.lowBatteryThreshold,
    }
  }, [robot])

  useEffect(() => {
    if (robot) {
      robotForm.setFieldsValue({
        ...robotInitialValues,
        voice: pickVoiceId(robot.voice),
      })
      deletedWorkingDayIdsRef.current = new Set()
      const energyVal = robot.lowBatteryThreshold ?? robot.minEnergy
      setGeneral((prev) => ({
        ...prev,
        schedules: buildGeneralSchedulesFromRobot(robot),
        minEnergy: energyVal ? String(energyVal) : prev.minEnergy,
        energyUnit: prev.energyUnit || robot.energyUnit || '%',
      }))
    }
  }, [robot, robotInitialValues, robotForm])

  const handleWorkingScheduleRemove = useCallback(
    (row: WorkingScheduleFormRow) => {
      if (row.source === 'backend' && row.id) {
        deletedWorkingDayIdsRef.current.add(row.id)
      }
    },
    [],
  )

  const { mutate: updateConfig, isPending: isUpdatingConfig } = useMutation({
    mutationFn: async (values: RobotFormValues) => {
      if (!robotId) throw new Error('Mã robot không hợp lệ')
      const workingSchedules =
        (robotForm.getFieldValue('workingSchedules') as
          | WorkingScheduleFormRow[]
          | undefined) ?? []

      const normalizeHHmm = (val: string): string => {
        if (!val) return ''
        const t = val.includes('T') ? val.split('T')[1] : val
        return t.slice(0, 5)
      }

      const normalizeDayOfWeek = (val: unknown): number[] => {
        if (Array.isArray(val)) {
          return val
            .map((v) =>
              typeof v === 'object' && v !== null && 'value' in v
                ? Number(v.value)
                : Number(v),
            )
            .filter((v) => Number.isFinite(v))
        }
        const single = Number(val)
        return Number.isFinite(single) ? [single] : []
      }

      const normalizedRows = workingSchedules
        .map((row) => ({
          id: row.id,
          source: row.source,
          createdByAction: row.createdByAction === true,
          dayOfWeek: normalizeDayOfWeek(row.dayOfWeek),
          startTime: row.startTime?.format
            ? row.startTime.format('HH:mm')
            : normalizeHHmm(String(row.startTime ?? '')),
          endTime: row.endTime?.format
            ? row.endTime.format('HH:mm')
            : normalizeHHmm(String(row.endTime ?? '')),
        }))
        .filter(
          (row) =>
            row.dayOfWeek.length > 0 &&
            Boolean(row.startTime) &&
            Boolean(row.endTime),
        )

      const createdList = normalizedRows
        .filter((row) => row.source === 'created')
        .map((row) => ({
          dayOfWeek: row.dayOfWeek,
          startTime: row.startTime,
          endTime: row.endTime,
        }))
      const deletedIdList = Array.from(deletedWorkingDayIdsRef.current)
      const backendRowsById = new Map(
        (robot?.robotWorkingDays ?? [])
          .filter((item) => item.id)
          .map((item) => [
            item.id,
            {
              dayOfWeek: normalizeDayOfWeek(item.dayOfWeek),
              startTime: normalizeHHmm(item.startTime),
              endTime: normalizeHHmm(item.endTime),
              status: item.status ?? 1,
            },
          ]),
      )

      const updatedList = normalizedRows
        .filter(
          (row) =>
            row.source === 'backend' &&
            Boolean(row.id) &&
            !deletedWorkingDayIdsRef.current.has(row.id as string),
        )
        .flatMap((row) => {
          const old = backendRowsById.get(row.id as string)
          if (!old) return []
          const isChanged =
            old.startTime !== row.startTime ||
            old.endTime !== row.endTime ||
            old.dayOfWeek.toString() !== row.dayOfWeek.toString()
          if (!isChanged) return []
          return [
            {
              id: row.id as string,
              dayOfWeek: row.dayOfWeek,
              startTime: row.startTime,
              endTime: row.endTime,
              status: old.status,
            },
          ]
        })

      if (
        createdList.length > 0 ||
        updatedList.length > 0 ||
        deletedIdList.length > 0
      ) {
        await updateRobotWorkingDays(robotId, {
          createdList,
          updatedList,
          deletedIdList,
        })
      }

      await updateConfigRobot(
        robotId,
        buildRobotPayload({
          ...values,
          diagramId: robot?.diagramInfo?.id || undefined,
        }),
      )
      return { ok: true }
    },
    onSuccess: () => {
      notification.success({ title: 'Lưu cấu hình robot thành công!' })
      queryClient.invalidateQueries({ queryKey: ['robots'] })
      window.history.back()
    },
  })

  return {
    robot,
    robotForm,
    robotInitialValues,
    general,
    isUpdatingConfig,
    showActivateButton,
    hasEnoughInfo,
    activateTooltip,
    liveValues,
    setLiveValues,
    updateConfig,
    handleWorkingScheduleRemove,
    refetchDetail,
  }
}
